import { useEffect, useState } from "react";
import {
  fetchAdminMediaKitStats,
  refreshAutomaticMediaKitStats,
  updateTikTokMediaKitStats,
} from "../../api/mediaKit";
import { PLATFORM_LABELS, formatCompactNumber, formatUpdatedDate } from "../../mediaKit/model";
import AdminPublishBar from "./AdminPublishBar";
import { CategoryRepeater, CountryRepeater, PairRepeater } from "./AdminRepeaters";

const blankTikTok = {
  followersCount: "",
  views28d: "",
  discoveryPercent: "",
  femalePercent: "",
  age1844Percent: "",
  countries: [],
  periodStart: "",
  periodEnd: "",
};

export default function MediaKitEditor({ content, saving, dirty, onChange, onDiscard, onSubmit }) {
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [tiktok, setTikTok] = useState(blankTikTok);
  const [savedTikTok, setSavedTikTok] = useState(blankTikTok);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState({ type: "", text: "" });

  const loadStats = async () => {
    const rows = await fetchAdminMediaKitStats();
    setStats(rows);
    const saved = rows.find((row) => row.platform === "tiktok") || blankTikTok;
    setTikTok({ ...saved, countries: [...(saved.countries || [])] });
    setSavedTikTok({ ...saved, countries: [...(saved.countries || [])] });
  };

  useEffect(() => {
    loadStats()
      .catch((error) => setMessage({ type: "error", text: error.message }))
      .finally(() => setStatsLoading(false));
  }, []);

  const updateTikTok = (field, value) => setTikTok((current) => ({ ...current, [field]: value }));
  const tiktokDirty = JSON.stringify(tiktok) !== JSON.stringify(savedTikTok);

  async function saveTikTok(event) {
    event.preventDefault();
    setBusy("tiktok");
    setMessage({ type: "", text: "" });
    try {
      const saved = await updateTikTokMediaKitStats(tiktok);
      const next = { ...saved, countries: [...(saved.countries || [])] };
      setTikTok(next);
      setSavedTikTok(next);
      setStats((current) => current.map((stat) => stat.platform === "tiktok" ? saved : stat));
      setMessage({ type: "success", text: "TikTok media-kit data published." });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setBusy("");
    }
  }

  async function refreshAutomatic() {
    setBusy("refresh");
    setMessage({ type: "", text: "" });
    try {
      const result = await refreshAutomaticMediaKitStats();
      await loadStats();
      const preserved = result.results?.filter((item) => item.status !== "updated") || [];
      setMessage({
        type: preserved.length ? "warning" : "success",
        text: preserved.length
          ? `${result.updated} sources refreshed; previous data was preserved for ${preserved.map((item) => PLATFORM_LABELS[item.platform]).join(", ")}.`
          : "YouTube, Instagram, and Facebook data refreshed.",
      });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setBusy("");
    }
  }

  return (
    <div className="admin-media-kit-stack">
      {message.text && <p className={`admin-message admin-inline-message ${message.type}`} role={message.type === "error" ? "alert" : "status"}>{message.text}</p>}

      <section className="admin-editor admin-social-status">
        <div className="admin-editor-heading">
          <div><p className="admin-kicker">Audience data</p><h2>Platform statistics</h2><p>Automatic sources retain their last successful snapshot. TikTok is maintained manually.</p></div>
          <button type="button" onClick={refreshAutomatic} disabled={Boolean(busy)} data-busy={busy === "refresh"}>{busy === "refresh" ? "Refreshing…" : "Refresh Google & Meta"}</button>
        </div>
        <div className="admin-platform-status-grid" aria-busy={statsLoading || undefined}>
          {statsLoading ? ["youtube", "instagram", "tiktok", "facebook"].map((platform) => (
            <article className="admin-status-skeleton" key={platform}><strong>{PLATFORM_LABELS[platform]}</strong><span /><span /><small>Loading current data…</small></article>
          )) : stats.map((stat) => (
            <article key={stat.platform}>
              <strong>{PLATFORM_LABELS[stat.platform]}</strong>
              <span>{formatCompactNumber(stat.followersCount)} followers</span>
              <span>{formatCompactNumber(stat.views28d)} views</span>
              <small>Updated {formatUpdatedDate(stat.capturedAt)} · {stat.source.replaceAll("_", " ")}</small>
            </article>
          ))}
        </div>
      </section>

      <form className="admin-editor" onSubmit={saveTikTok}>
        <div className="admin-editor-heading"><div><p className="admin-kicker">Manual source</p><h2>TikTok data</h2><p>Enter values from the same completed 28-day reporting period.</p></div></div>
        <fieldset className="admin-form-section">
          <legend>Audience and reach</legend>
          <div className="admin-stat-grid admin-stat-grid-wide">
            <label>Followers<input type="number" min="0" value={tiktok.followersCount ?? ""} onChange={(event) => updateTikTok("followersCount", event.target.value)} required /></label>
            <label>Views in 28 days<input type="number" min="0" value={tiktok.views28d ?? ""} onChange={(event) => updateTikTok("views28d", event.target.value)} required /></label>
            <label>Non-follower views %<input type="number" min="0" max="100" step="0.1" value={tiktok.discoveryPercent ?? ""} onChange={(event) => updateTikTok("discoveryPercent", event.target.value)} /></label>
            <label>Female audience %<input type="number" min="0" max="100" step="0.1" value={tiktok.femalePercent ?? ""} onChange={(event) => updateTikTok("femalePercent", event.target.value)} /></label>
            <label>Audience aged 18–44 %<input type="number" min="0" max="100" step="0.1" value={tiktok.age1844Percent ?? ""} onChange={(event) => updateTikTok("age1844Percent", event.target.value)} /></label>
            <label>Period start<input type="date" value={tiktok.periodStart || ""} onChange={(event) => updateTikTok("periodStart", event.target.value)} required /></label>
            <label>Period end<input type="date" value={tiktok.periodEnd || ""} onChange={(event) => updateTikTok("periodEnd", event.target.value)} required /></label>
          </div>
        </fieldset>
        <CountryRepeater items={tiktok.countries} onChange={(countries) => updateTikTok("countries", countries)} />
        <AdminPublishBar dirty={tiktokDirty} saving={busy === "tiktok"} label="Publish TikTok data" onDiscard={() => setTikTok({ ...savedTikTok, countries: [...(savedTikTok.countries || [])] })} />
      </form>

      <form className="admin-editor" onSubmit={onSubmit}>
        <div className="admin-editor-heading">
          <div><p className="admin-kicker">Page content</p><h2>Media Kit copy</h2><p>Changes remain private until you publish them, then update both the page and downloadable PDF.</p></div>
          <a href="/media-kit" target="_blank" rel="noopener noreferrer">View page</a>
        </div>

        <fieldset className="admin-form-section">
          <legend>Hero and introduction</legend>
          <label>Kicker<input value={content.kicker} onChange={(event) => onChange("kicker", event.target.value)} required /></label>
          <label>Page title<input value={content.title} onChange={(event) => onChange("title", event.target.value)} required /></label>
          <label>Introduction<textarea rows="4" value={content.intro} onChange={(event) => onChange("intro", event.target.value)} required /></label>
          <label>Hero image URL<input type="url" value={content.heroImageUrl} onChange={(event) => onChange("heroImageUrl", event.target.value)} required /></label>
          <label>Hero image description<input value={content.heroImageAlt} onChange={(event) => onChange("heroImageAlt", event.target.value)} required /></label>
        </fieldset>

        <fieldset className="admin-form-section">
          <legend>Brand fit</legend>
          <label>Heading<input value={content.brandFitTitle} onChange={(event) => onChange("brandFitTitle", event.target.value)} required /></label>
          <label>Introduction<textarea rows="3" value={content.brandFitIntro} onChange={(event) => onChange("brandFitIntro", event.target.value)} required /></label>
          <PairRepeater legend="Brand-fit points" itemLabel="Brand-fit point" items={content.brandFitPoints} onChange={(value) => onChange("brandFitPoints", value)} />
          <label>Discovery heading<input value={content.discoveryTitle} onChange={(event) => onChange("discoveryTitle", event.target.value)} required /></label>
          <label>Discovery introduction<textarea rows="3" value={content.discoveryIntro} onChange={(event) => onChange("discoveryIntro", event.target.value)} required /></label>
        </fieldset>

        <fieldset className="admin-form-section">
          <legend>Audience narrative</legend>
          <label>Audience heading<input value={content.audienceTitle} onChange={(event) => onChange("audienceTitle", event.target.value)} required /></label>
          <label>Audience introduction<textarea rows="3" value={content.audienceIntro} onChange={(event) => onChange("audienceIntro", event.target.value)} required /></label>
          <label>Audience supporting copy<textarea rows="3" value={content.audienceDetail} onChange={(event) => onChange("audienceDetail", event.target.value)} required /></label>
          <CategoryRepeater items={content.categories} onChange={(value) => onChange("categories", value)} />
        </fieldset>

        <fieldset className="admin-form-section admin-platform-copy">
          <legend>Platform descriptions</legend>
          {Object.entries(content.platformDescriptions).map(([platform, description]) => <label key={platform}>{PLATFORM_LABELS[platform]} description<input value={description} onChange={(event) => onChange("platformDescriptions", { ...content.platformDescriptions, [platform]: event.target.value })} required /></label>)}
        </fieldset>

        <fieldset className="admin-form-section">
          <legend>Partnership opportunities</legend>
          <label>Heading<input value={content.opportunitiesTitle} onChange={(event) => onChange("opportunitiesTitle", event.target.value)} required /></label>
          <label>Introduction<textarea rows="3" value={content.opportunitiesIntro} onChange={(event) => onChange("opportunitiesIntro", event.target.value)} required /></label>
          <PairRepeater legend="Campaign ideas" itemLabel="Opportunity" items={content.opportunities} onChange={(value) => onChange("opportunities", value)} />
        </fieldset>

        <fieldset className="admin-form-section">
          <legend>Contact and call to action</legend>
          <label>Call-to-action heading<input value={content.ctaTitle} onChange={(event) => onChange("ctaTitle", event.target.value)} required /></label>
          <label>Call-to-action copy<textarea rows="3" value={content.ctaText} onChange={(event) => onChange("ctaText", event.target.value)} required /></label>
          <label>Contact email<input type="email" value={content.contactEmail} onChange={(event) => onChange("contactEmail", event.target.value)} required /></label>
          <label>Website URL<input type="url" value={content.websiteUrl} onChange={(event) => onChange("websiteUrl", event.target.value)} required /></label>
        </fieldset>

        <AdminPublishBar dirty={dirty} saving={saving} label="Publish Media Kit" onDiscard={onDiscard} />
      </form>
    </div>
  );
}
